import React from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import i18n from "@/i18n";

import cls from "./MemberProfileEdit.module.scss";

import avatarPng from "@/assets/UI/avatar.png";

import GenderMaleIconMini from "@/components/icons/GenderMaleIconMini";
import PaymentIconMini from "@/components/icons/PaymentIconMini";
import GenderWomanIconMini from "@/components/icons/GenderWomanIconMini";
import GenderNonBinaryIconMini from "@/components/icons/GenderNonBinaryIconMini";
import GenderTransManIconMini from "@/components/icons/GenderTransManIconMini";
import GenderTransWomanIconMini from "@/components/icons/GenderTransWomanIconMini";
import GenderGenderfluidIconMini from "@/components/icons/GenderGenderfluidIconMini";
import GenderSelfDescribeIconMini from "@/components/icons/GenderSelfDescribeIconMini";
import LoginIconMini from "@/components/icons/LoginIconMini";
import PortalIconMini from "@/components/icons/PortalIconMini";
import WhoWeAreIconMini from "@/components/icons/WhoWeAreIconMini";
import MyProfileIconMini from "@/components/icons/MyProfileIconMini";

const GENDER_ICON_MAP = {
  man: GenderMaleIconMini,
  woman: GenderWomanIconMini,
  non_binary: GenderNonBinaryIconMini,
  trans_man: GenderTransManIconMini,
  trans_woman: GenderTransWomanIconMini,
  genderfluid: GenderGenderfluidIconMini,
  self_describe: GenderSelfDescribeIconMini,
  not_select: GenderSelfDescribeIconMini,
};

function getGenderIcon(gender) {
  return GENDER_ICON_MAP[gender] || GenderSelfDescribeIconMini;
}

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: "easeOut" },
  },
};

const navVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.15, duration: 0.28, ease: "easeOut" },
  },
};

const MemberProfileEdit = () => {
  const navigate = useNavigate();

  // Context comes from MemberLayout <Outlet context={{ ... }} />
  const outletCtx = useOutletContext() || {};

  const {
    isCouple = false,
    rawGender = "not_select",
    rawPartnerGender = null,
    avatarSrc,
    rawUsername,
  } = outletCtx;

  // Final avatar fallback
  const finalAvatarSrc = avatarSrc || avatarPng;

  const GenderIcon = getGenderIcon(rawGender);
  const PartnerGenderIcon = rawPartnerGender
    ? getGenderIcon(rawPartnerGender)
    : null;

  const handleBack = () => {
    // Pending page currently lives at "/"
    navigate("/lounge/my-profile");
  };

  const memberTypeLabel = isCouple
    ? i18n.t("memberMyProfile.memberType.couple")
    : i18n.t("memberMyProfile.memberType.single");

  return (
    <section className={cls.page}>
      <div className={cls.shell}>
        {/* Back to Pending */}
        <div className={cls.backRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            <span className={cls.backIcon} aria-hidden="true">
              ←
            </span>
            <span>{i18n.t("memberMyProfile.backToMyProfile")}</span>
          </button>
        </div>

        {/* Profile Card */}
        <Motion.div
          className={cls.card}
          variants={cardVariants}
          initial="hidden"
          animate="show"
        >
          <div className={cls.cardGlow} aria-hidden="true" />

          {/* Header */}
          <header className={cls.header}>
            <div className={cls.avatarBlock}>
              <div className={cls.avatarShell}>
                <div className={cls.avatarRing} />
                <img
                  src={finalAvatarSrc}
                  alt={i18n.t("memberMyProfile.avatarAlt")}
                  className={cls.avatarImg}
                />
              </div>

              <div className={cls.genderBadges} aria-hidden="true">
                {isCouple ? (
                  <div className={cls.genderPair}>
                    <span className={cls.genderBadgeItem}>
                      <GenderIcon size={18} />
                    </span>
                    {PartnerGenderIcon && (
                      <span className={cls.genderBadgeItemSecondary}>
                        <PartnerGenderIcon size={18} />
                      </span>
                    )}
                  </div>
                ) : (
                  <span className={cls.genderSolo}>
                    <GenderIcon size={20} />
                  </span>
                )}
              </div>
            </div>

            <div className={cls.meta}>
              <p className={cls.memberType}>{memberTypeLabel}</p>
              <h1 className={cls.title}>
                {i18n.t("memberMyProfile.hi")} {rawUsername}{" "}
              </h1>
              <h3 className={cls.title2}>{i18n.t("memberMyProfile.title")}</h3>
              <p className={cls.subtitle}>
                {i18n.t("memberMyProfile.subtitle")}
              </p>

              <div className={cls.chipsRow}>
                <span className={cls.chipPrimary}>
                  <MyProfileIconMini
                    size={16}
                    title={i18n.t("memberMyProfile.chips.profileOverview")}
                    data-preserve
                  />
                  <span>{i18n.t("memberMyProfile.chips.profileOverview")}</span>
                </span>

                {isCouple && (
                  <span className={cls.chipSoft}>
                    {i18n.t("memberMyProfile.chips.coupleMode")}
                  </span>
                )}

                <span className={cls.chipSoft}>
                  {i18n.t("memberMyProfile.chips.privateOnly")}
                </span>
              </div>
            </div>
          </header>

          {/* Navigation Buttons */}
          <Motion.div
            className={cls.navGrid}
            variants={navVariants}
            initial="hidden"
            animate="show"
          >
            <Link
              to="/lounge/my-profile/edit/login-info"
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <LoginIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.login.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.login.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.login.hint")}
                </span>
              </div>
            </Link>
            <Link
              to="/lounge/my-profile/edit/member-info"
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <MyProfileIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.member.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.member.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.member.hint")}
                </span>
              </div>
            </Link>
            <Link
              to="/lounge/my-profile/edit/personal-info"
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <WhoWeAreIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.personal.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.personal.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.personal.hint")}
                </span>
              </div>
            </Link>

            <Link
              to="/lounge/my-profile/edit/lifestyle-info"
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <PortalIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.lifestyle.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.lifestyle.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.lifestyle.hint")}
                </span>
              </div>
            </Link>

            <Link to="/lounge/my-profile/edit/partners" className={cls.navBtn}>
              <div className={cls.navIcon}>
                <MyProfileIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.partners.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.partners.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.partners.hint")}
                </span>
              </div>
            </Link>
            <Link to="/lounge/my-profile/edit/payments" className={cls.navBtn}>
              <div className={cls.navIcon}>
                <PaymentIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.myPayments.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.myPayments.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.myPayments.hint")}
                </span>
              </div>
            </Link>
            <Link
              to="/lounge/my-profile/edit/payments/new"
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <PaymentIconMini
                  size={20}
                  title={i18n.t("memberMyProfile.actions.payment.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("memberMyProfile.actions.payment.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("memberMyProfile.actions.payment.hint")}
                </span>
              </div>
            </Link>
          </Motion.div>

          {/* Footnote */}
          <footer className={cls.footerNote}>
            {i18n.t("memberMyProfile.footerNote")}
          </footer>
        </Motion.div>
      </div>
    </section>
  );
};

export default MemberProfileEdit;
